import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Toolbar,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Chip,
} from '@mui/material';
import { Add, Remove, Visibility, VisibilityOff } from '@mui/icons-material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LogoCarousel from '../components/LogoCarousel';
import communityPic from '../img/IMG_1018.JPEG';

const RunClubAuth = () => {
  const [currentView, setCurrentView] = useState('signup');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(null);
  const [status, setStatus] = useState({ message: '', type: '' });

  // Login form data
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Registration form data
  const [formData, setFormData] = useState({
    companyName: '',
    mainContactName: '',
    mainContactEmail: '',
    phoneNumber: '',
    companySize: '',
    streetName: '',
    town: '',
    postcode: '',
    instagram: '',
    tiktok: '',
    linkedin: '',
    logo: null,
    password: '',
    confirmPassword: ''
  });

  // Additional contacts
  const [additionalContacts, setAdditionalContacts] = useState([
    { name: '', email: '' }
  ]);

  const [errors, setErrors] = useState({});

  const companySizes = [
    '1-10 members',
    '11-25 members',
    '26-50 members',
    '51-100 members',
    '100+ members'
  ];

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          logo: reader.result
        }));
      };
      reader.readAsDataURL(file);
      
      // Clear error when valid file is selected
      if (errors.logo) {
        setErrors(prev => ({
          ...prev,
          logo: ''
        }));
      }
    } else if (file) {
      setErrors(prev => ({
        ...prev,
        logo: 'Please select a valid image file'
      }));
    }
  };

  const handleContactChange = (index, field, value) => {
    const newContacts = [...additionalContacts];
    newContacts[index][field] = value;
    setAdditionalContacts(newContacts);
    
    // Clear related errors
    if (errors[`contact_${index}_${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`contact_${index}_${field}`]: ''
      }));
    }
  };

  const addContact = () => {
    setAdditionalContacts([...additionalContacts, { name: '', email: '' }]);
  };

  const removeContact = (index) => {
    if (additionalContacts.length > 1) {
      setAdditionalContacts(additionalContacts.filter((_, i) => i !== index));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.logo) {
      newErrors.logo = 'Company logo is required';
    }
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.mainContactName.trim()) newErrors.mainContactName = 'Main contact name is required';
    if (!formData.mainContactEmail.trim()) {
      newErrors.mainContactEmail = 'Main contact email is required';
    } else if (!validateEmail(formData.mainContactEmail)) {
      newErrors.mainContactEmail = 'Please enter a valid email address';
    }
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.companySize) newErrors.companySize = 'Company size is required';
    if (!formData.streetName.trim()) newErrors.streetName = 'Street name is required';
    if (!formData.town.trim()) newErrors.town = 'Town is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Validate additional contacts
    additionalContacts.forEach((contact, index) => {
      if (contact.name.trim() && !contact.email.trim()) {
        newErrors[`contact_${index}_email`] = 'Email is required when name is provided';
      } else if (contact.email.trim() && !validateEmail(contact.email)) {
        newErrors[`contact_${index}_email`] = 'Please enter a valid email address';
      }
      if (contact.email.trim() && !contact.name.trim()) {
        newErrors[`contact_${index}_name`] = 'Name is required when email is provided';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitted(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Simulate successful login
      setStatus({
        message: 'Login successful! Redirecting to dashboard...',
        type: 'success'
      });
      setSuccess(true);
      
    } catch (error) {
      setStatus({
        message: 'Login failed. Please try again.',
        type: 'error'
      });
      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitted(true);
      setSuccess(false);
      return;
    }

    setIsLoading(true);
    setSubmitted(true);
    
    try {
      // Prepare data for API submission
      const submissionData = {
        companyName: formData.companyName,
        mainContactName: formData.mainContactName,
        mainContactEmail: formData.mainContactEmail,
        phoneNumber: formData.phoneNumber,
        companySize: formData.companySize,
        location: {
          name: formData.streetName,
          city: formData.town,
          postcode: formData.postcode
        },
        socialMediaDetails: {
          instagram: formData.instagram || '',
          tiktok: formData.tiktok || '',
          linkedin: formData.linkedin || ''
        },
        password: formData.password,
        additionalContacts: additionalContacts.filter(contact => 
          contact.name.trim() && contact.email.trim()
        ),
        logo: formData.logo
      };

      console.log('Registration data:', submissionData);
      
      // Make API call
      const response = await fetch('http://https://lyncfit-backend-deploy-production.up.railway.app/api/auth/organisations/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus({
          message: 'Registration submitted successfully! Awaiting approval.',
          type: 'success'
        });
        setSuccess(true);
        setCurrentView('pending-approval');
      } else {
        setStatus({
          message: result.message || 'Registration failed. Please try again.',
          type: 'error'
        });
        setSuccess(false);
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      setStatus({
        message: 'Registration failed. Please check your connection and try again.',
        type: 'error'
      });
      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const textFieldStyle = {
    backgroundColor: '#f0f0f0',
    borderRadius: '50px',
    mt: 1,
    input: {
    color: 'black',
    },
    '& .MuiInputBase-input': {
      color: 'black', 
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "none",
      },
      // "&:hover fieldset": {
      //   border: "1px solid #767676",
      //   borderRadius: '50px',
      //   backgroundColor: '#dedede',
      // },
      // "&.Mui-focused fieldset": {
      //   border: "2px solid #3366cc",
      // },
    },
  };

  const selectStyle = {
    backgroundColor: '#f0f0f0',
    borderRadius: '50px',
    mt: 1,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "1px solid #767676",
        borderRadius: '50px',
        backgroundColor: '#dedede',
      },
      "&.Mui-focused fieldset": {
        border: "2px solid #3366cc",
      },
    },
  };

  const renderLogin = () => (
    <Container maxWidth="md">
      <Box sx={{ py: 4, bgcolor: "white", p: 3}}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mt: { xs: 0, md: 8 }, textAlign:'center' }}>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: "medium", 
                  color: "#3366cc", 
                  fontFamily: "'Nunito Sans', sans-serif", 
                  fontSize: '3.5rem',
                  mt: 4,
                  textAlign: 'center'
                }}
              >
                Welcome Back
              </Typography>
              <Typography variant="body1" sx={{ mt: 2, textAlign: 'center'}}>
                Sign in to your run club portal!
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box component="div" sx={{ mt: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#3366cc", fontWeight: "bold" }}
                  >
                    Email Address (required)
                  </Typography>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    required
                    variant="outlined"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    sx={textFieldStyle}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#3366cc", fontWeight: "bold" }}
                  >
                    Password (required)
                  </Typography>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    variant="outlined"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    sx={textFieldStyle}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button 
                    variant="contained" 
                    onClick={handleLogin}
                    disabled={isLoading}
                    color="primary"
                    sx={{ 
                      borderRadius: '20px', 
                      px: 5, 
                      py: 1.5,
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    New run club?{' '}
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setCurrentView('signup')}
                      sx={{ color: '#3366cc' }}
                    >
                      Register here
                    </Link>
                  </Typography>
                </Grid>
              </Grid>

              {submitted && success === false && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {status.message}
                </Alert>
              )}
              {submitted && success === true && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {status.message}
                </Alert>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );

  const renderSignup = () => (
    <Container maxWidth="lg">
      <Box sx={{ py: 4, bgcolor: "white", p: 3, borderRadius: 2 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: "medium", 
            color: "#3366cc", 
            fontFamily: "'Nunito Sans', sans-serif", 
            fontSize: '3rem',
            textAlign: 'left',
            mb: 4,
          }}
        >
          Register Your Community
        </Typography>
        
        <Box component="div" sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            {/* Company Information */}
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ color: "#3366cc", fontWeight: "bold", mb: 2 }}>
                Company Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Company Name (required)
              </Typography>
              <TextField
                fullWidth
                name="companyName"
                required
                variant="outlined"
                value={formData.companyName}
                onChange={handleChange}
                error={!!errors.companyName}
                helperText={errors.companyName}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Company Size (required)
              </Typography>
              <FormControl fullWidth sx={selectStyle}>
                <Select
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleChange}
                  error={!!errors.companySize}
                  displayEmpty
                >
                  <MenuItem value="">Select company size</MenuItem>
                  {companySizes.map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.companySize && (
                <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                  {errors.companySize}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Company Logo (required)
              </Typography>
              <Box sx={{ mt: 1 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload"
                  type="file"
                  onChange={handleLogoChange}
                />
                <label htmlFor="logo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    sx={{
                      borderRadius: '20px',
                      px: 3,
                      py: 1.5,
                      borderColor: errors.logo ? '#d32f2f' : '#3366cc',
                      color: errors.logo ? '#d32f2f' : '#3366cc',
                      backgroundColor: '#f0f0f0',
                      '&:hover': {
                        backgroundColor: '#dedede',
                        borderColor: errors.logo ? '#d32f2f' : '#767676',
                      }
                    }}
                  >
                    {formData.logo ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                </label>
                {formData.logo && (
                  <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                    Logo uploaded successfully
                  </Typography>
                )}
                {errors.logo && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, ml: 2 }}>
                    {errors.logo}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Main Contact */}
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ color: "#3366cc", fontWeight: "bold", mb: 2, mt: 4 }}>
                Main Contact
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Name (required)
              </Typography>
              <TextField
                fullWidth
                name="mainContactName"
                required
                variant="outlined"
                value={formData.mainContactName}
                onChange={handleChange}
                error={!!errors.mainContactName}
                helperText={errors.mainContactName}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Email (required)
              </Typography>
              <TextField
                fullWidth
                name="mainContactEmail"
                type="email"
                required
                variant="outlined"
                value={formData.mainContactEmail}
                onChange={handleChange}
                error={!!errors.mainContactEmail}
                helperText={errors.mainContactEmail}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Phone Number (required)
              </Typography>
              <TextField
                fullWidth
                name="phoneNumber"
                type="tel"
                required
                variant="outlined"
                value={formData.phoneNumber}
                onChange={handleChange}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                sx={textFieldStyle}
              />
            </Grid>

            {/* Additional Contacts */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4 }}>
                <Typography variant="h5" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                  Additional Contacts
                </Typography>
                <Button
                  startIcon={<Add />}
                  onClick={addContact}
                  sx={{ color: '#3366cc' }}
                >
                  Add Contact
                </Button>
              </Box>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            {additionalContacts.map((contact, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12} md={5}>
                  <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                    Name
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={contact.name}
                    onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                    error={!!errors[`contact_${index}_name`]}
                    helperText={errors[`contact_${index}_name`]}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    variant="outlined"
                    value={contact.email}
                    onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                    error={!!errors[`contact_${index}_email`]}
                    helperText={errors[`contact_${index}_email`]}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {additionalContacts.length > 1 && (
                    <IconButton
                      onClick={() => removeContact(index)}
                      sx={{ color: '#d32f2f', mt: 3 }}
                    >
                      <Remove />
                    </IconButton>
                  )}
                </Grid>
              </React.Fragment>
            ))}

            {/* Location */}
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ color: "#3366cc", fontWeight: "bold", mb: 2, mt: 4 }}>
                Primary Location
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Street Name (required)
              </Typography>
              <TextField
                fullWidth
                name="streetName"
                required
                variant="outlined"
                value={formData.streetName}
                onChange={handleChange}
                error={!!errors.streetName}
                helperText={errors.streetName}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Town (required)
              </Typography>
              <TextField
                fullWidth
                name="town"
                required
                variant="outlined"
                value={formData.town}
                onChange={handleChange}
                error={!!errors.town}
                helperText={errors.town}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Postcode (required)
              </Typography>
              <TextField
                fullWidth
                name="postcode"
                required
                variant="outlined"
                value={formData.postcode}
                onChange={handleChange}
                error={!!errors.postcode}
                helperText={errors.postcode}
                sx={textFieldStyle}
              />
            </Grid>

            {/* Social Media */}
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ color: "#3366cc", fontWeight: "bold", mb: 2, mt: 4 }}>
                Social Media (Optional)
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Instagram
              </Typography>
              <TextField
                fullWidth
                name="instagram"
                variant="outlined"
                value={formData.instagram}
                onChange={handleChange}
                placeholder="@runclub"
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                TikTok
              </Typography>
              <TextField
                fullWidth
                name="tiktok"
                variant="outlined"
                value={formData.tiktok}
                onChange={handleChange}
                placeholder="@runclub"
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                LinkedIn
              </Typography>
              <TextField
                fullWidth
                name="linkedin"
                variant="outlined"
                value={formData.linkedin}
                onChange={handleChange}
                placeholder="company/runclub"
                sx={textFieldStyle}
              />
            </Grid>

            {/* Password */}
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ color: "#3366cc", fontWeight: "bold", mb: 2, mt: 4 }}>
                Security
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Password (required)
              </Typography>
              <TextField
                fullWidth
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                variant="outlined"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                sx={textFieldStyle}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#3366cc", fontWeight: "bold" }}>
                Confirm Password (required)
              </Typography>
              <TextField
                fullWidth
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                variant="outlined"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                sx={textFieldStyle}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            {/* Submit Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                {/* <Button 
                  variant="outlined" 
                  onClick={() => setCurrentView('login')}
                  sx={{ 
                    borderRadius: '20px', 
                    px: 5, 
                    py: 1.5,
                    borderColor: '#3366cc',
                    color: '#3366cc'
                  }}
                >
                  Back to Login
                </Button> */}
                <Button 
                  variant="contained" 
                  onClick={handleSignup}
                  disabled={isLoading}
                  color="primary"
                  sx={{ 
                    borderRadius: '20px', 
                    px: 5, 
                    py: 1.5,
                  }}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Register Run Club'}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {submitted && success === false && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {status.message || 'Please fix the errors above and try again.'}
            </Alert>
          )}
          {submitted && success === true && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {status.message}
            </Alert>
          )}
        </Box>
      </Box>
    </Container>
  );

  const renderPendingApproval = () => (
    <Container maxWidth="md">
      <Box sx={{ py: 4, bgcolor: "white", p: 3, borderRadius: 2, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: "medium", 
            color: "#3366cc", 
            fontFamily: "'Nunito Sans', sans-serif", 
            fontSize: '3.5rem',
            mb: 4,
          }}
        >
          Registration Submitted!
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
          Thank you for registering your run club.
        </Typography>

        <Paper sx={{ p: 3, mb: 4, bgcolor: '#f0f8ff' }}>
          <Typography variant="h6" sx={{ color: '#3366cc', mb: 2 }}>
            What happens next?
          </Typography>
          <Box sx={{ textAlign: 'left', maxWidth: '400px', mx: 'auto' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Our team will review your application within 24-48 hours
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • We'll approve your run club and set up your account
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • All contacts will receive password setup emails
            </Typography>
            <Typography variant="body2">
              • Your team can then access the platform
            </Typography>
          </Box>
        </Paper>

        {/* <Button 
          variant="contained" 
          onClick={() => setCurrentView('login')}
          color="primary"
          sx={{ 
            borderRadius: '20px', 
            px: 5, 
            py: 1.5,
          }}
        >
          Back to Login
        </Button> */}
      </Box>
    </Container>
  );

  const renderIntro = () => (
    <Container maxWidth="lg">
  {/* Intro Section */}
  <Box sx={{ py: 6, px: 4, bgcolor: "white" }}>
    <Grid container spacing={4} alignItems="center">
      {/* Text Section */}
      <Grid item xs={12} md={6}>
        <Box sx={{ mt: 0 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: "bold", 
              color: "#3366cc", 
              fontFamily: "'Nunito Sans', sans-serif", 
              fontSize: '2.8rem',
              mb: 2
            }}
          >
            Bring Your Community to LyncFit
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            LyncFit connects fitness communities through accountability, motivation, and meaningful social experiences. By registering your run club or fitness organisation, you'll gain access to a powerful platform that helps you:
          </Typography>

          {/* Bullet Points */}
          <ul style={{ marginLeft: '1.2em', marginBottom: '16px' }}>
            <li>Create and manage events</li>
            <li>Engage your members through group chats</li>
            <li>Grow your reach using our analytics</li>
          </ul>

          <Typography variant="body1" sx={{ mb: 2 }}>
            Once registered, your events will appear on the LyncFit mobile app, where users can discover, join, and interact with your community. You'll also unlock tools for insights, communication, and rewards to keep your members motivated and coming back.
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
            Let’s make fitness more social, together.<br />
            <strong>Fill out the form below to get started.</strong>
          </Typography>
        </Box>
      </Grid>

      {/* Image Section */}
      <Grid item xs={12} md={6}>
        <Box
          component="img"
          src= {communityPic}
          alt="LyncFit Community"
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: 2,
            boxShadow: 3,
          }}
        />
      </Grid>
    </Grid>
  </Box>
</Container>


    
  );


  return (
    <>
      <Box sx={{flex: 1, py: 4 }}>
        <Header />
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
        {renderIntro()}
        <Box sx={{ my: 10, maxWidth: 1160, mx: 'auto' }}>
          <LogoCarousel />
        </Box>
        {currentView === 'login' && renderLogin()}
        {currentView === 'signup' && renderSignup()}
        {currentView === 'pending-approval' && renderPendingApproval()}
        <Footer />
      </Box>
    </Box>
    </>
  );
};

export default RunClubAuth;
